/** Gestion des comptes admin : administrateurs et chefs d'équipe (admin uniquement). */

import { useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { Header } from '../components/layout/Header';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Modal } from '../components/ui/Modal';
import { Table, type Column } from '../components/ui/Table';
import { Badge } from '../components/ui/Badge';
import {
  useUsers,
  useCreateUser,
  useDeleteUser,
  type AppUser,
  type UserRole,
} from '../hooks/useUsers';
import { ACTIVITY_LABEL, type EmployeeActivity } from '../types';

const ROLE_LABEL: Record<UserRole, string> = {
  admin: 'Administrateur',
  manager: "Chef d'équipe",
};

// Équipes proposées à la création d'un chef d'équipe
const TEAMS: EmployeeActivity[] = ['boulangerie', 'traiteur', 'chambre_froide', 'cleaning', 'driving', 'office'];

export function UsersPage() {
  const { data: users, isLoading } = useUsers();
  const createUser = useCreateUser();
  const deleteUser = useDeleteUser();

  const [open, setOpen] = useState(false);
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState<UserRole>('manager');
  const [activity, setActivity] = useState<EmployeeActivity>('boulangerie');
  const [error, setError] = useState<string | null>(null);

  const openCreate = () => {
    setUsername('');
    setEmail('');
    setPassword('');
    setRole('manager');
    setActivity('boulangerie');
    setError(null);
    setOpen(true);
  };

  const handleCreate = async () => {
    setError(null);
    if (username.trim().length < 3) {
      setError('Identifiant : 3 caractères minimum');
      return;
    }
    if (password.length < 8) {
      setError('Mot de passe : 8 caractères minimum');
      return;
    }
    try {
      await createUser.mutateAsync({
        username: username.trim(),
        email: email.trim(),
        password,
        role,
        activity: role === 'manager' ? activity : null,
      });
      setOpen(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erreur');
    }
  };

  const handleDelete = async (u: AppUser) => {
    if (!window.confirm(`Supprimer le compte « ${u.username} » ?`)) return;
    try {
      await deleteUser.mutateAsync(u.id);
    } catch (e) {
      window.alert(e instanceof Error ? e.message : 'Erreur');
    }
  };

  const columns: Column<AppUser>[] = [
    {
      key: 'username',
      header: 'Identifiant',
      render: (u) => <span className="font-medium text-gray-900">{u.username}</span>,
    },
    {
      key: 'email',
      header: 'Email',
      render: (u) => <span className="text-gray-600">{u.email}</span>,
    },
    {
      key: 'role',
      header: 'Rôle',
      render: (u) => (
        <Badge variant={u.role === 'admin' ? 'success' : 'neutral'}>{ROLE_LABEL[u.role]}</Badge>
      ),
    },
    {
      key: 'activity',
      header: 'Équipe',
      render: (u) =>
        u.activity ? (
          <span className="text-gray-600">{ACTIVITY_LABEL[u.activity]}</span>
        ) : (
          <span className="text-gray-400">—</span>
        ),
    },
    {
      key: 'actions',
      header: '',
      render: (u) => (
        <Button variant="ghost" size="sm" onClick={() => handleDelete(u)}>
          <Trash2 size={13} className="text-red-500" />
        </Button>
      ),
    },
  ];

  return (
    <div>
      <Header
        title="Utilisateurs"
        action={
          <Button size="sm" onClick={openCreate}>
            <Plus size={14} className="mr-1" /> Nouveau chef d'équipe
          </Button>
        }
      />
      <p className="text-xs text-stone-500 mb-4">
        Chaque chef d'équipe est rattaché à une équipe et gère uniquement les plannings
        de celle-ci. Les administrateurs ont accès à toute l'application.
      </p>

      <Card>
        <Table
          columns={columns}
          data={users ?? []}
          loading={isLoading}
          emptyMessage="Aucun utilisateur"
        />
      </Card>

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title="Nouveau compte"
        footer={
          <>
            <Button variant="secondary" onClick={() => setOpen(false)}>
              Annuler
            </Button>
            <Button onClick={handleCreate} loading={createUser.isPending}>
              Créer
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <div className="space-y-1">
            <label className="block text-xs font-medium text-gray-600">Rôle</label>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value as UserRole)}
              className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm
                         focus:outline-none focus:ring-1 focus:ring-accent focus:border-accent"
            >
              <option value="manager">Chef d'équipe</option>
              <option value="admin">Administrateur</option>
            </select>
          </div>
          {role === 'manager' && (
            <div className="space-y-1">
              <label className="block text-xs font-medium text-gray-600">Équipe</label>
              <select
                value={activity}
                onChange={(e) => setActivity(e.target.value as EmployeeActivity)}
                className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm
                           focus:outline-none focus:ring-1 focus:ring-accent focus:border-accent"
              >
                {TEAMS.map((t) => (
                  <option key={t} value={t}>
                    {ACTIVITY_LABEL[t]}
                  </option>
                ))}
              </select>
            </div>
          )}
          <Input
            label="Identifiant"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            placeholder="jdupont"
          />
          <Input
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="chef@exemple.fr"
          />
          <Input
            label="Mot de passe"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="8 caractères minimum"
          />
          {error && <p className="text-xs text-red-500">{error}</p>}
        </div>
      </Modal>
    </div>
  );
}
